import React, { useEffect, useState, useRef } from 'react';
import { BuildingInsights, DataLayers } from '../../types/solar';
import { Image, Mountain, Sun, Loader2, Check } from 'lucide-react';
import { apiClient } from '../../services/api';
import * as GeoTIFF from 'geotiff';

interface GeoTIFFCanvasRendererProps {
  buildingInsights: BuildingInsights;
  dataLayers: DataLayers | null;
  selectedLayer: LayerType;
  showMask: boolean;
  onProgressUpdate?: (progress: DownloadProgress) => void;
  onLayersLoaded?: (layers: any) => void;
}

type LayerType = 'rgb' | 'dsm' | 'flux';

interface GeoTIFFData {
  width: number;
  height: number;
  rasters: number[][];
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

interface DownloadProgress {
  rgb: 'pending' | 'downloading' | 'complete' | 'error';
  mask: 'pending' | 'downloading' | 'complete' | 'error';
  dsm: 'pending' | 'downloading' | 'complete' | 'error';
  flux: 'pending' | 'downloading' | 'complete' | 'error';
}

export default function GeoTIFFCanvasRenderer({ 
  buildingInsights, 
  dataLayers,
  selectedLayer,
  showMask,
  onProgressUpdate,
  onLayersLoaded
}: GeoTIFFCanvasRendererProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Cache for downloaded GeoTIFFs
  const [cachedLayers, setCachedLayers] = useState<{
    rgb?: GeoTIFFData;
    mask?: GeoTIFFData;
    dsm?: GeoTIFFData;
    flux?: GeoTIFFData;
  }>({});

  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    rgb: 'pending',
    mask: 'pending',
    dsm: 'pending',
    flux: 'pending',
  });
  
  // Notify parent of progress updates
  useEffect(() => {
    if (onProgressUpdate) {
      onProgressUpdate(downloadProgress);
    }
  }, [downloadProgress, onProgressUpdate]);

  // Download and parse GeoTIFF
  const downloadGeoTIFF = async (url: string, layerName: string): Promise<GeoTIFFData | null> => {
    try {
      
      const startTime = performance.now();

      // Check if this is a GCS URL (already signed) or Google Solar API URL (needs proxy)
      const isGCSUrl = url.includes('storage.googleapis.com') || url.includes('storage.cloud.google.com');
      
      let arrayBuffer: ArrayBuffer;
      if (isGCSUrl) {
        // GCS signed URLs can be fetched directly (no proxy needed)
        const response = await fetch(url);
        if (!response.ok) {
          console.warn(`⚠️  ${layerName} not available (${response.status})`);
          return null;
        }
        arrayBuffer = await response.arrayBuffer();
      } else {
        // Google Solar API URLs need to go through proxy (adds API key)
        const response = await apiClient.get('/api/solar/geotiff-proxy', {
          params: { url },
          responseType: 'arraybuffer',
        });
        arrayBuffer = response.data;
      }

      const elapsed = performance.now() - startTime;

      // Parse GeoTIFF
      const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer);
      const image = await tiff.getImage();
      const rasters = await image.readRasters();

      // Get bounding box
      const bbox = image.getBoundingBox();
      
      return {
        width: rasters.width,
        height: rasters.height,
        rasters: [...Array(rasters.length).keys()].map((i) =>
          Array.from(rasters[i] as any)
        ),
        bounds: {
          south: bbox[1],
          west: bbox[0],
          north: bbox[3],
          east: bbox[2],
        },
      };
    } catch (error) {
      console.error(`❌ Error downloading ${layerName}:`, error);
      console.warn(`⚠️  ${layerName} will be skipped - continuing with available layers`);
      return null;
    }
  };

  // Auto-download all layers on mount
  useEffect(() => {
    if (!dataLayers) {
      return;
    }

    const downloadAllLayers = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Phase 1: RGB + Mask (priority for quick visual feedback)
        
        // Download RGB
        setDownloadProgress(prev => ({ ...prev, rgb: 'downloading' }));
        const rgbData = await downloadGeoTIFF(dataLayers.rgbUrl, 'RGB');
        if (rgbData) {
          setCachedLayers(prev => ({ ...prev, rgb: rgbData }));
          setDownloadProgress(prev => ({ ...prev, rgb: 'complete' }));
        } else {
          setDownloadProgress(prev => ({ ...prev, rgb: 'error' }));
        }
        
        // Download Mask
        setDownloadProgress(prev => ({ ...prev, mask: 'downloading' }));
        const maskData = await downloadGeoTIFF(dataLayers.maskUrl, 'Mask');
        if (maskData) {
          setCachedLayers(prev => ({ ...prev, mask: maskData }));
          setDownloadProgress(prev => ({ ...prev, mask: 'complete' }));
        } else {
          setDownloadProgress(prev => ({ ...prev, mask: 'error' }));
        }


        // Phase 2: DSM + Flux (parallel download)
        
        setDownloadProgress(prev => ({ ...prev, dsm: 'downloading', flux: 'downloading' }));

        const [dsmData, fluxData] = await Promise.all([
          downloadGeoTIFF(dataLayers.dsmUrl, 'DSM'),
          downloadGeoTIFF(dataLayers.annualFluxUrl, 'Flux'),
        ]);

        // Update layers (null values are OK - will show as "N/A")
        if (dsmData) {
          setCachedLayers(prev => ({ ...prev, dsm: dsmData }));
          setDownloadProgress(prev => ({ ...prev, dsm: 'complete' }));
        } else {
          setDownloadProgress(prev => ({ ...prev, dsm: 'error' }));
        }
        
        if (fluxData) {
          setCachedLayers(prev => ({ ...prev, flux: fluxData }));
          setDownloadProgress(prev => ({ ...prev, flux: 'complete' }));
        } else {
          setDownloadProgress(prev => ({ ...prev, flux: 'error' }));
        }

        setIsLoading(false);

      } catch (err) {
        console.error('❌ Error downloading GeoTIFFs:', err);
        setError(err instanceof Error ? err.message : 'Failed to download GeoTIFFs');
        setIsLoading(false);
        
        // Mark failed layers
        if (!cachedLayers.rgb) setDownloadProgress(prev => ({ ...prev, rgb: 'error' }));
        if (!cachedLayers.mask) setDownloadProgress(prev => ({ ...prev, mask: 'error' }));
        if (!cachedLayers.dsm) setDownloadProgress(prev => ({ ...prev, dsm: 'error' }));
        if (!cachedLayers.flux) setDownloadProgress(prev => ({ ...prev, flux: 'error' }));
      }
    };

    downloadAllLayers();
  }, [dataLayers]);

  // Render to canvas when layer selection or mask toggle changes
  useEffect(() => {
    if (!cachedLayers.rgb) return; // Wait for at least RGB

    const layerData = selectedLayer === 'rgb' ? cachedLayers.rgb :
                      selectedLayer === 'dsm' ? cachedLayers.dsm :
                      cachedLayers.flux;

    if (!layerData) {
      return;
    }

    const maskData = showMask ? cachedLayers.mask : undefined;

    if (selectedLayer === 'rgb') {
      renderRGB(layerData, maskData);
    } else if (selectedLayer === 'dsm') {
      renderPalette(layerData, rainbowPalette, maskData);
    } else if (selectedLayer === 'flux') {
      renderPalette(layerData, ironPalette, maskData);
    }
  }, [selectedLayer, showMask, cachedLayers]);

  // Render functions (simplified from original)
  const renderRGB = (rgb: GeoTIFFData, mask?: GeoTIFFData): void => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.width = rgb.width;
    canvas.height = rgb.height;

    const ctx = canvas.getContext('2d')!;
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const idx = y * canvas.width + x;
        const imgIdx = idx * 4;

        img.data[imgIdx + 0] = rgb.rasters[0][idx]; // Red
        img.data[imgIdx + 1] = rgb.rasters[1][idx]; // Green
        img.data[imgIdx + 2] = rgb.rasters[2][idx]; // Blue
        img.data[imgIdx + 3] = mask ? mask.rasters[0][idx] * 255 : 255; // Alpha
      }
    }

    ctx.putImageData(img, 0, 0);
  };

  // Color palettes
  const rainbowPalette = ['#0000FF', '#00FFFF', '#00FF00', '#FFFF00', '#FF0000'];
  const ironPalette = ['#000000', '#660000', '#CC0000', '#FF6600', '#FFFF00'];

  const createPalette = (hexColors: string[]): { r: number; g: number; b: number }[] => {
    const rgb = hexColors.map(hex => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    });
    
    const size = 256;
    const step = (rgb.length - 1) / (size - 1);
    return Array(size).fill(0).map((_, i) => {
      const index = i * step;
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      const t = index - lower;
      return {
        r: rgb[lower].r + t * (rgb[upper].r - rgb[lower].r),
        g: rgb[lower].g + t * (rgb[upper].g - rgb[lower].g),
        b: rgb[lower].b + t * (rgb[upper].b - rgb[lower].b),
      };
    });
  };

  const renderPalette = (data: GeoTIFFData, colors: string[], mask?: GeoTIFFData): void => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.width = data.width;
    canvas.height = data.height;

    const ctx = canvas.getContext('2d')!;
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const palette = createPalette(colors);
    const rasters = data.rasters[0];

    // Determine min/max
    const sortedValues = Array.from(rasters).sort((a, b) => a - b);
    const minVal = sortedValues[0];
    const maxVal = sortedValues[sortedValues.length - 1];

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const idx = y * canvas.width + x;
        const imgIdx = idx * 4;

        const normalizedValue = Math.max(0, Math.min(1, (rasters[idx] - minVal) / (maxVal - minVal)));
        const paletteIndex = Math.round(normalizedValue * (palette.length - 1));
        const color = palette[paletteIndex];

        img.data[imgIdx + 0] = color.r;
        img.data[imgIdx + 1] = color.g;
        img.data[imgIdx + 2] = color.b;
        img.data[imgIdx + 3] = mask ? mask.rasters[0][idx] * 255 : 255;
      }
    }

    ctx.putImageData(img, 0, 0);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Canvas Display - Full Screen */}
      <div className="flex-1 relative bg-black flex items-center justify-center overflow-auto">
        {isLoading && !cachedLayers.rgb && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-10">
            <Loader2 className="h-10 w-10 animate-spin text-[#EBFD03]" />
            <span className="ml-3 text-lg text-white">Downloading RGB...</span>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-900 bg-opacity-75 z-10 p-4 text-center">
            <p className="text-red-200 text-lg">Error: {error}</p>
          </div>
        )}
        <canvas ref={canvasRef} className="max-w-full max-h-full object-contain" />
      </div>
    </div>
  );
}

// Export helper for progress icon
export function getProgressIcon(status: 'pending' | 'downloading' | 'complete' | 'error') {
  if (status === 'complete') return <Check className="h-4 w-4 text-green-500" />;
  if (status === 'downloading') return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
  if (status === 'error') return <span className="text-red-500 text-xs">✗</span>;
  return <span className="text-gray-400 text-xs">○</span>;
}
